import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  IconButton,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import { PitchDetector } from 'pitchy';
import PitchTracker from './PitchTracker';
import { hapticNoteAdvance } from '../../utils/haptics';
import { playCorrect } from '../../utils/audioEngine';

const GameLevel = ({ level, onComplete, onBack, onRequestCalibration }) => {
  const [gameState, setGameState] = useState('intro'); // intro, calibration, countdown, playing, complete
  const [currentNoteIndex, setCurrentNoteIndex] = useState(0);
  const [countdown, setCountdown] = useState(3);
  const [isRecording, setIsRecording] = useState(false);
  const [accuracyHistory, setAccuracyHistory] = useState([]);
  const [audioStream, setAudioStream] = useState(null);
  const [currentPitch, setCurrentPitch] = useState(0);
  const [displayPitch, setDisplayPitch] = useState(0); // Holds last valid pitch for ~400ms to prevent flicker
  const [noteProgress, setNoteProgress] = useState(0);
  const [currentConfidence, setCurrentConfidence] = useState(0); // Track pitch confidence
  const [startTime, setStartTime] = useState(null); // Track game start time
  const holdPitchTimerRef = useRef(null); // Timer to clear displayPitch after dropout
  
  // Per-note accuracy tracking
  const [noteAccuracyData, setNoteAccuracyData] = useState([]);
  const noteAccuracyDataRef = useRef([]); // Array of arrays - one per note
  const currentNoteStartIndexRef = useRef(0); // Track where current note's samples start
  
  // Note lifecycle tracking
  const noteStartTimeRef = useRef(null); // When current note started
  const [noteState, setNoteState] = useState('preparing'); // preparing, active, complete
  const noteStateRef = useRef('preparing'); // Ref for animation loop access
  
  // Sync ref with state for animation loop access
  useEffect(() => {
    noteStateRef.current = noteState;
  }, [noteState]);
  
  // Calibration state
  const [userSaFrequency, setUserSaFrequency] = useState(null);
  const userSaFrequencyRef = useRef(null); // mirror of userSaFrequency for use inside closures/callbacks
  const [calibrationPitches, setCalibrationPitches] = useState([]);
  const [isCalibrating, setIsCalibrating] = useState(false);
  const [savedLogs, setSavedLogs] = useState(null); // Preserve logs after game ends
  const [calibrationDriftWarning, setCalibrationDriftWarning] = useState(false); // True when user is consistently off-target
  // Rolling drift tracker: accumulates signed cents deviations; if consistently large in one direction => drift
  const driftTrackingRef = useRef({ samples: [], warningShown: false });

  const [playingRefNoteIdx, setPlayingRefNoteIdx] = useState(null); // Which note button is ringing
  const [hasHeardNote, setHasHeardNote] = useState(false); // True once user taps any note preview button
  const [previewIsPlaying, setPreviewIsPlaying] = useState(false); // sequence preview running
  const [previewNoteIdx, setPreviewNoteIdx] = useState(-1);         // active note index during sequence
  const [noteChipProgress, setNoteChipProgress] = useState({});     // { [noteIdx]: 0–100 } fill during preview
  // Landscape detection — used to show full-screen immersive tracker
  const [isLandscape, setIsLandscape] = useState(false);
  useEffect(() => {
    const check = () => setIsLandscape(window.matchMedia('(orientation: landscape)').matches);
    check();
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, []);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const referenceToneRef = useRef(null);   // { oscillator, gain, ownCtx } while a reference note is ringing
  const refAudioCtxRef  = useRef(null);    // Standalone AudioContext used when mic isn't running
  const refToneEnabledRef = useRef(false); // True when the user has toggled the reference tone ON
  const refFadeTimerRef = useRef(null);    // setTimeout id for the in-flight fade-out; cancel if new tone starts early
  const previewTimersRef = useRef([]);    // all setTimeout IDs for the sequence preview (cancellable)
  const previewIntervalRef = useRef(null); // setInterval for per-note progress tracking during preview
  const smoothedPitchRef = useRef(0); // For exponential smoothing
  const pitchHistoryRef = useRef([]); // Rolling window for median filtering
  const lastValidPitchTimeRef = useRef(0); // Timestamp of last accepted pitch (to detect genuine gaps)
  const accuracyHistoryBufferRef = useRef([]); // Buffer for accuracy smoothing
  const accuracyHistoryRef = useRef([]); // Store accuracy immediately (not async state)
  const debugLogRef = useRef([]); // Collect all pitch detection logs
  const sessionStartTimeRef = useRef(null); // Track timing
  // Advanced pitch detection refs
  const expectedFreqRangeRef = useRef({ min: 80, max: 500 });
  const octaveCorrectionHistoryRef = useRef([]);
  const confidenceHistoryRef = useRef([]);
  const pitchDetectorRef = useRef(null); // MPM detector — recreated when fftSize changes

  // Calculate note frequencies relative to user's Sa
  const currentNote = useMemo(() => {
    const note = level.notes[currentNoteIndex];
    if (!userSaFrequency) return note;
    
    // Calculate the ratio from standard Sa (261.63 Hz) to the note
    const standardSa = 261.63;
    const ratio = note.frequency / standardSa;
    
    // Apply the same ratio to user's Sa
    return {
      ...note,
      frequency: userSaFrequency * ratio,
      originalFrequency: note.frequency
    };
  }, [level.notes, currentNoteIndex, userSaFrequency]);

  // All notes with calibration applied — passed to PitchTracker so its canvas
  // uses the same transposed frequencies as the text display above it.
  const calibratedNotes = useMemo(() => {
    if (!userSaFrequency) return level.notes;
    const standardSa = 261.63;
    return level.notes.map((note) => ({
      ...note,
      frequency: userSaFrequency * (note.frequency / standardSa),
      originalFrequency: note.frequency,
    }));
  }, [level.notes, userSaFrequency]);

  // Ref for currentNote to avoid closure issues in pitch detection loop
  const currentNoteRef = useRef(currentNote);
  
  // Sync ref whenever currentNote changes
  useEffect(() => {
    currentNoteRef.current = currentNote;
  }, [currentNote]);
  
  // Sync ref whenever userSaFrequency changes so closures always read the live value
  useEffect(() => {
    userSaFrequencyRef.current = userSaFrequency;
  }, [userSaFrequency]);

  const totalNotes = level.notes.length;

  // Load calibration from localStorage and keep in sync with any external changes.
  // This handles three cases:
  //   1. Normal mount — reads whatever is already in localStorage
  //   2. App-level CalibrationModal completes — dispatches 'calibrationChanged'
  //   3. S3 progress sync after login writes updated calibration to localStorage
  useEffect(() => {
    const updateCalibration = () => {
      const savedCalibration = localStorage.getItem('userSaFrequency');
      if (savedCalibration) {
        const frequency = parseFloat(savedCalibration);
        if (!isNaN(frequency) && frequency >= 80 && frequency <= 500) {
          setUserSaFrequency(frequency);
        }
      }
    };

    // Initial load
    updateCalibration();

    // Re-run whenever CalibrationModal saves a new value or S3 sync updates it
    window.addEventListener('calibrationChanged', updateCalibration);
    window.addEventListener('storage', updateCalibration);

    return () => {
      window.removeEventListener('calibrationChanged', updateCalibration);
      window.removeEventListener('storage', updateCalibration);
    };
  }, []);

  // Reset "has heard note" whenever the intro screen is (re)shown so users
  // always listen before starting — even after recalibration or a retry.
  useEffect(() => {
    if (gameState === 'intro') setHasHeardNote(false);
  }, [gameState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (holdPitchTimerRef.current) {
        clearTimeout(holdPitchTimerRef.current);
        holdPitchTimerRef.current = null;
      }
      // Cancel any in-flight fade-out timer first
      if (refFadeTimerRef.current) {
        clearTimeout(refFadeTimerRef.current);
        refFadeTimerRef.current = null;
      }
      // Stop any reference tone that may still be ringing
      if (referenceToneRef.current) {
        try { referenceToneRef.current.oscillator.stop(); } catch (_) {}
        if (referenceToneRef.current.ownCtx) {
          try { referenceToneRef.current.ownCtx.close(); } catch (_) {}
        }
        referenceToneRef.current = null;
      }
      // refAudioCtxRef is always the same object as referenceToneRef.ownCtx — already closed above
      refAudioCtxRef.current = null;
      // Cancel any in-progress note-preview sequence
      previewTimersRef.current.forEach(clearTimeout);
      previewTimersRef.current = [];
      if (previewIntervalRef.current) { clearInterval(previewIntervalRef.current); previewIntervalRef.current = null; }
      stopRecording();
      stopPitchDetection();
    };
  }, []);

  // Start pitch detection when audio stream becomes available
  useEffect(() => {
    if (audioStream && gameState === 'playing') {
      startPitchDetection();
    } else if (audioStream && gameState === 'calibration') {
      startCalibrationDetection();
    }
  }, [audioStream, gameState]);

  // Calibration: detect user's comfortable Sa frequency
  const startCalibration = async () => {
    setGameState('calibration');
    setIsCalibrating(true);
    setCalibrationPitches([]);
    await startRecording();
  };

  const startCalibrationDetection = async () => {
    if (!audioStream) return;

    const audioContext = new (window.AudioContext || window.webkitAudioContext)({ latencyHint: 'interactive' });
    audioContextRef.current = audioContext;
    if (audioContext.state === 'suspended') await audioContext.resume();

    // Adaptive fftSize for Bluetooth HFP (see startPitchDetection)
    const sr = audioContext.sampleRate;
    const fftSize = sr <= 8000 ? 512 : sr <= 16000 ? 1024 : 2048;

    const analyser = audioContext.createAnalyser();
    analyser.fftSize = fftSize;
    analyserRef.current = analyser;

    const source = audioContext.createMediaStreamSource(audioStream);
    source.connect(analyser);

    pitchDetectorRef.current = PitchDetector.forFloat32Array(fftSize);
    const buffer = new Float32Array(fftSize);

    const detectPitch = () => {
      if (gameState !== 'calibration') return;

      analyser.getFloatTimeDomainData(buffer);
      const [pitch, clarity] = pitchDetectorRef.current.findPitch(buffer, audioContext.sampleRate);

      if (clarity >= 0.6 && pitch >= 80 && pitch <= 500) {
        setCalibrationPitches((prev) => [...prev, pitch]);
        setCurrentPitch(pitch);
      }

      animationFrameRef.current = requestAnimationFrame(detectPitch);
    };

    detectPitch();
  };

  const finishCalibration = () => {
    if (calibrationPitches.length === 0) {
      alert('No voice detected. Please sing a comfortable note and try again.');
      setGameState('intro');
      stopRecording();
      stopPitchDetection();
      return;
    }

    // Interquartile-mean: discard bottom + top 25% of samples, average the middle.
    // More robust than median when early warm-up wobbles skew the distribution.
    const sorted = [...calibrationPitches].sort((a, b) => a - b);
    const q1 = Math.floor(sorted.length * 0.25);
    const q3 = Math.ceil(sorted.length * 0.75);
    const middle = sorted.slice(q1, q3);
    const iqrMean = middle.reduce((s, v) => s + v, 0) / middle.length;
    
    setUserSaFrequency(iqrMean);
    // Save to localStorage for persistence
    localStorage.setItem('userSaFrequency', iqrMean.toString());
    // Notify other components that calibration changed
    window.dispatchEvent(new Event('calibrationChanged'));
    setIsCalibrating(false);
    
    // Calibration complete

    stopRecording();
    stopPitchDetection();
    
    // Start countdown after calibration
    startCountdown();
  };

  const recalibrate = async () => {
    // Stop any ongoing recording or pitch detection
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    // Stop and cleanup audio context
    if (audioContextRef.current) {
      const ctx = audioContextRef.current;
      audioContextRef.current = null;
      try { ctx.close().catch(() => {}); } catch (_) {}
    }
    
    // Stop recording if active and cleanup tracks
    if (audioStream) {
      audioStream.getTracks().forEach((track) => track.stop());
      setAudioStream(null);
    }
    
    if (mediaRecorderRef.current) {
      try {
        if (mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      } catch (e) {
        // Ignore if already stopped
      }
      mediaRecorderRef.current = null;
    }
    
    setIsRecording(false);
    
    // Clear calibration from localStorage and state
    setUserSaFrequency(null);
    localStorage.removeItem('userSaFrequency');
    // Notify other components that calibration was cleared
    window.dispatchEvent(new Event('calibrationChanged'));
    setCalibrationPitches([]);
    setIsCalibrating(false);
    
    // Clear all refs
    audioChunksRef.current = [];
    smoothedPitchRef.current = 0;
    pitchHistoryRef.current = [];
    accuracyHistoryBufferRef.current = [];
    accuracyHistoryRef.current = [];
    debugLogRef.current = [];
    octaveCorrectionHistoryRef.current = [];
    confidenceHistoryRef.current = [];
    
    // Reset game state to intro - user will need to calibrate next time they start
    setGameState('intro');
    setCurrentNoteIndex(0);
    setNoteProgress(0);
    setAccuracyHistory([]);
    setCurrentPitch(0);
    if (holdPitchTimerRef.current) {
      clearTimeout(holdPitchTimerRef.current);
      holdPitchTimerRef.current = null;
    }
    setDisplayPitch(0);
    setCalibrationDriftWarning(false);
    driftTrackingRef.current = { samples: [], warningShown: false };
  };

  // ---------------------------------------------------------------------------
  // Reference tone: tap a note button to hear the calibrated pitch
  // Toggle ON/OFF. While ON, note advances smoothly retune the oscillator.
  // ---------------------------------------------------------------------------

  // Internal helper — immediately kill whatever is currently playing.
  // Cancels any in-flight fade timer so it can never close a future context.
  const _killReferenceTone = () => {
    // Cancel any pending fade-out timer FIRST
    if (refFadeTimerRef.current) {
      clearTimeout(refFadeTimerRef.current);
      refFadeTimerRef.current = null;
    }
    refToneEnabledRef.current = false; // always reset — keep in sync with referenceToneRef
    if (referenceToneRef.current) {
      const { oscillator, ownCtx } = referenceToneRef.current;
      referenceToneRef.current = null;
      try { oscillator.stop(); } catch (_) {}
      if (ownCtx) {
        try { ownCtx.close(); } catch (_) {}
      }
      refAudioCtxRef.current = null;
    }
  };

  const stopReferenceTone = () => {
    _killReferenceTone();
    setPlayingRefNoteIdx(null);
  };

  // ── Sequence preview: plays all level notes in order at actual durations ──────
  // Lets users hear the full exercise before singing, even for long multi-note runs.

  const stopSequencePreview = () => {
    previewTimersRef.current.forEach(clearTimeout);
    previewTimersRef.current = [];
    if (previewIntervalRef.current) { clearInterval(previewIntervalRef.current); previewIntervalRef.current = null; }
    _killReferenceTone();
    setPlayingRefNoteIdx(null);
    setPreviewIsPlaying(false);
    setPreviewNoteIdx(-1);
    setNoteChipProgress({});
  };

  const startSequencePreview = () => {
    stopSequencePreview();
    setPreviewIsPlaying(true);
    setHasHeardNote(true);

    let offset = 0;
    level.notes.forEach((note, idx) => {
      const noteStart = offset;
      const noteDur = note.duration;

      const startT = setTimeout(() => {
        setPreviewNoteIdx(idx);
        // Kill any previous tone first so the toggle-guard in playReferenceTone
        // never fires (handles repeated notes like Sa-Sa-Sa).
        _killReferenceTone();
        playReferenceTone(getCalibFreq(note), idx);

        // Progress bar: update every 40ms, fills over noteDur seconds
        if (previewIntervalRef.current) clearInterval(previewIntervalRef.current);
        const t0 = Date.now();
        previewIntervalRef.current = setInterval(() => {
          const pct = Math.min(100, ((Date.now() - t0) / (noteDur * 1000)) * 100);
          setNoteChipProgress({ [idx]: pct });
          if (pct >= 100) { clearInterval(previewIntervalRef.current); previewIntervalRef.current = null; }
        }, 40);
      }, noteStart * 1000);

      previewTimersRef.current.push(startT);
      offset += noteDur;
    });

    // Final cleanup after last note finishes
    const doneT = setTimeout(() => {
      _killReferenceTone();
      setPlayingRefNoteIdx(null);
      setPreviewIsPlaying(false);
      setPreviewNoteIdx(-1);
      setNoteChipProgress({});
    }, offset * 1000 + 200);
    previewTimersRef.current.push(doneT);
  };

  // Single-note tap: plays for the note's actual duration then auto-stops.
  const handleNotePreviewTap = (note, idx) => {
    // If this exact note is already playing → toggle off (stop)
    if (refToneEnabledRef.current && playingRefNoteIdx === idx && !previewIsPlaying) {
      stopSequencePreview();
      return;
    }
    stopSequencePreview();
    playReferenceTone(getCalibFreq(note), idx);
    setHasHeardNote(true);

    // Progress bar for the tapped note
    const dur = note.duration * 1000;
    const t0 = Date.now();
    if (previewIntervalRef.current) clearInterval(previewIntervalRef.current);
    previewIntervalRef.current = setInterval(() => {
      const pct = Math.min(100, ((Date.now() - t0) / dur) * 100);
      setNoteChipProgress({ [idx]: pct });
      if (pct >= 100) { clearInterval(previewIntervalRef.current); previewIntervalRef.current = null; }
    }, 40);

    // Auto-stop after duration
    const stopT = setTimeout(() => {
      stopReferenceTone();
      setNoteChipProgress({});
    }, dur);
    previewTimersRef.current = [stopT];
  };
  // ─────────────────────────────────────────────────────────────────────────────

  const playReferenceTone = (freq, noteIdx) => {
    // Tap same active button again → toggle OFF
    if (refToneEnabledRef.current && playingRefNoteIdx === noteIdx) {
      stopReferenceTone();
      return;
    }

    // Always kill the current tone and create a completely fresh AudioContext.
    // Reusing the same context (retune path) was silently failing on iOS WKWebView
    // when the context got suspended between taps — a new context always resumes
    // inside the current user-gesture handler and is guaranteed to play.
    _killReferenceTone();

    const ctx = new (window.AudioContext || window.webkitAudioContext)({ latencyHint: 'interactive' });
    refAudioCtxRef.current = ctx;

    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();

    oscillator.type = 'triangle';
    oscillator.connect(gain);
    gain.connect(ctx.destination);

    const startTone = () => {
      oscillator.frequency.setValueAtTime(freq, ctx.currentTime);
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.25, ctx.currentTime + 0.05);
      oscillator.start();
      referenceToneRef.current = { oscillator, gain, ownCtx: ctx };
      refToneEnabledRef.current = true;
      setPlayingRefNoteIdx(noteIdx);
    };

    // iOS WKWebView suspends AudioContext until a user-gesture chain — must await resume.
    if (ctx.state === 'suspended') {
      ctx.resume().then(startTone).catch(() => {});
    } else {
      startTone();
    }
  };

  // Helper: compute calibrated frequency for any note in the level
  const getCalibFreq = (note) => {
    const sa = userSaFrequencyRef.current;
    if (!sa) return note.frequency;
    return sa * (note.frequency / 261.63);
  };
  // ---------------------------------------------------------------------------

  const startCountdown = () => {
    stopReferenceTone(); // silence any preview before countdown begins
    setGameState('countdown');
    let count = 3;
    setCountdown(count);
    
    const interval = setInterval(() => {
      count--;
      if (count > 0) {
        setCountdown(count);
      } else {
        clearInterval(interval);
        startGame();
      }
    }, 1000);
  };

  const startGame = async () => {
    setGameState('playing');
    setCurrentNoteIndex(0);
    setAccuracyHistory([]);
    setNoteProgress(0);
    setCurrentPitch(0);
    setCurrentConfidence(0);
    if (holdPitchTimerRef.current) {
      clearTimeout(holdPitchTimerRef.current);
      holdPitchTimerRef.current = null;
    }
    setDisplayPitch(0);
    setCalibrationDriftWarning(false);
    driftTrackingRef.current = { samples: [], warningShown: false };
    setStartTime(Date.now()); // Track start time
    debugLogRef.current = []; // Clear previous logs
    accuracyHistoryRef.current = []; // Clear accuracy history
    sessionStartTimeRef.current = Date.now(); // Start timing
    
    // Initialize per-note tracking
    noteAccuracyDataRef.current = [];
    currentNoteStartIndexRef.current = 0;
    setNoteAccuracyData([]);
    
    // Initialize grace period
    setNoteState('preparing');
    noteStateRef.current = 'preparing'; // Sync ref immediately
    noteStartTimeRef.current = null;
    
    await startRecording();
    // Pitch detection will start automatically via useEffect when audioStream is set
  };

  const handleNoteProgress = useCallback((progress) => {
    setNoteProgress(progress);
    
    // Auto-advance when note completes
    if (progress >= 1) {
      if (currentNoteIndex < totalNotes - 1) {
        // Store completed note's accuracy data
        const noteAccuracy = accuracyHistoryRef.current.slice(currentNoteStartIndexRef.current);
        noteAccuracyDataRef.current.push(noteAccuracy);
        // No per-note sound on intermediate notes in multi-note levels —
        // it interrupts the singing flow. The level-complete sound covers it.
        currentNoteStartIndexRef.current = accuracyHistoryRef.current.length;
        
        // Reset pitch state for next note (prevents visual carryover)
        setCurrentPitch(0);
        setCurrentConfidence(0);
        smoothedPitchRef.current = 0;
        pitchHistoryRef.current = [];
        lastValidPitchTimeRef.current = 0;
        
        // Start grace period for next note  
        // Timer will be reset on next detectPitch frame when it sees noteStartTimeRef is null
        setNoteState('preparing');
        noteStateRef.current = 'preparing'; // Sync ref immediately
        noteStartTimeRef.current = null; // Will be reset on next frame
        
        // Move to next note
        setTimeout(() => {
          hapticNoteAdvance();
          const nextIdx = currentNoteIndex + 1;
          setCurrentNoteIndex(nextIdx);
          setNoteProgress(0);
          if (refToneEnabledRef.current) {
            const nextNote = level.notes[nextIdx];
            if (nextNote) {
              const sa = userSaFrequencyRef.current;
              const nextFreq = sa ? sa * (nextNote.frequency / 261.63) : nextNote.frequency;
              playReferenceTone(nextFreq, nextIdx);
            }
          }
        }, 100);
      } else {
        // Store final note's accuracy data and play feedback
        const finalNoteAccuracy = accuracyHistoryRef.current.slice(currentNoteStartIndexRef.current);
        noteAccuracyDataRef.current.push(finalNoteAccuracy);
        const finalAvgAcc = finalNoteAccuracy.length > 0
          ? finalNoteAccuracy.reduce((s, v) => s + v, 0) / finalNoteAccuracy.length
          : 0;
        // For single-note levels keep the quick correct/wrong cue;
        // for multi-note levels no sound is played at the end.
        if (totalNotes === 1) {
          if (finalAvgAcc >= 68) { playCorrect(); }
        }
        setNoteState('complete');
        setTimeout(() => {
          endGame();
        }, 100);
      }
    }
  }, [currentNoteIndex, totalNotes]);

  const endGame = async () => {
    _killReferenceTone(); // stop reference tone immediately when game ends
    await stopRecording();
    stopPitchDetection();
    
    // Save logs before state changes with per-note data
    const logsToSave = {
      debugLog: [...debugLogRef.current],
      accuracyHistory: [...accuracyHistoryRef.current],
      noteAccuracyData: [...noteAccuracyDataRef.current],
      metadata: {
        levelId: level.id,
        levelName: level.name,
        userSaFrequency: userSaFrequency,
        notes: level.notes
      }
    };
    setSavedLogs(logsToSave);
    setNoteAccuracyData(noteAccuracyDataRef.current);

    // ── Background S3 log upload ────────────────────────────────────────────
    // Fire-and-forget: never blocks the UI. On failure the log is queued in
    // localStorage (up to 20 entries) and retried on the next upload attempt.
    (async () => {
      try {
        const stored = localStorage.getItem('evcs_user');
        if (!stored) return; // not logged in — skip
        const { sub: userId, email: userEmail } = JSON.parse(stored);

        const { uploadSessionLog } = await import('../../services/api');

        // Drain any pending (previously-failed) logs first
        try {
          const pending = JSON.parse(localStorage.getItem('evcs_pending_logs') || '[]');
          if (pending.length > 0) {
            for (const entry of pending) {
              await uploadSessionLog(userId, entry.log, null, userEmail);
            }
            localStorage.removeItem('evcs_pending_logs');
          }
        } catch (_) {} // queue drain is best-effort

        // Upload this session's log
        await uploadSessionLog(userId, logsToSave, null, userEmail);
      } catch (err) {
        // Persist for retry on next successful upload
        try {
          const pending = JSON.parse(localStorage.getItem('evcs_pending_logs') || '[]');
          pending.push({ timestamp: Date.now(), log: logsToSave });
          localStorage.setItem('evcs_pending_logs', JSON.stringify(pending.slice(-20)));
        } catch (_) {}
      }
    })();
    // ────────────────────────────────────────────────────────────────────────

    // Show finished state first (with download button)
    setGameState('finished');
    
    // Don't call onComplete immediately - let user download logs first
    // onComplete will be called when user clicks "View Results"
  };

  // Calculate score for a single note.
  //
  // Design goals:
  //   • Short notes (1-1.5s) must not be punished by the vocal attack phase.
  //   • The evaluation window scales with note duration so the scoring is fair
  //     across the full range of lesson durations.
  //   • The boundary at 3s (old hard-coded window) is removed — no step-function.
  //
  // Algorithm:
  //   1. Trim the first ~200ms (or 15% of duration, whichever is smaller) —
  //      these are attack frames where the voice hasn't settled.
  //   2. Compute a proportional best-window: clamp(duration × 0.7, 0.5s, 3s).
  //      The window slides over the trimmed samples and the highest average wins.
  //   3. Minimum sample gate: if fewer than 10 samples remain after trimming,
  //      there isn't enough data to score — return 0.
  const calculateNoteScore = (noteAccuracy, noteDuration) => {
    if (!noteAccuracy || noteAccuracy.length === 0) return 0;

    // Count valid samples (above 10% accuracy) before any trimming
    const validSamples = noteAccuracy.filter(acc => acc > 10).length;
    const validPercentage = (validSamples / noteAccuracy.length) * 100;
    // If less than 20% of samples are valid, user didn't really sing this note
    if (validPercentage < 20) return 0;

    const samplesPerSecond = noteAccuracy.length / noteDuration;

    // Attack trim: skip first min(200ms, 15% of duration) worth of samples
    const attackTrimSec = Math.min(0.2, noteDuration * 0.15);
    const attackTrimSamples = Math.floor(samplesPerSecond * attackTrimSec);
    const trimmed = noteAccuracy.slice(attackTrimSamples);

    // Minimum sample gate after trimming
    if (trimmed.length < 10) {
      // Not enough data — use whatever we have rather than zero-ing the note
      return trimmed.length > 0
        ? trimmed.reduce((s, v) => s + v, 0) / trimmed.length
        : 0;
    }

    // Proportional best-window: clamp(duration × 0.7, 0.5s, 3s)
    const windowSec = Math.min(3, Math.max(0.5, noteDuration * 0.7));
    const windowSize = Math.floor(samplesPerSecond * windowSec);

    // If trimmed samples shorter than window, just average everything
    if (trimmed.length <= windowSize) {
      return trimmed.reduce((s, v) => s + v, 0) / trimmed.length;
    }

    // Slide window over trimmed samples and take the best stretch
    let bestScore = 0;
    for (let i = 0; i <= trimmed.length - windowSize; i++) {
      const windowAvg = trimmed.slice(i, i + windowSize).reduce((s, v) => s + v, 0) / windowSize;
      if (windowAvg > bestScore) bestScore = windowAvg;
    }
    return bestScore;
  };

  const showResults = () => {
    // Calculate final score using per-note scoring
    const logsToUse = savedLogs || {
      debugLog: debugLogRef.current,
      accuracyHistory: accuracyHistoryRef.current,
      noteAccuracyData: noteAccuracyDataRef.current
    };
    
    let finalScore = 0;
    let noteScores = [];
    
    // Per-note scoring if we have the data
    if (logsToUse.noteAccuracyData && logsToUse.noteAccuracyData.length === level.notes.length) {
      // Calculate score for each note
      noteScores = level.notes.map((note, index) => {
        const noteAccuracy = logsToUse.noteAccuracyData[index];
        const noteScore = calculateNoteScore(noteAccuracy, note.duration);
        return {
          noteDisplay: note.display,
          score: noteScore,
          samples: noteAccuracy ? noteAccuracy.length : 0
        };
      });
      
      // Calculate weighted average (equal weight for now, can adjust later)
      const totalWeight = noteScores.length;
      const weightedSum = noteScores.reduce((sum, ns) => sum + ns.score, 0);
      const averageScore = weightedSum / totalWeight;
      
      // Calculate consistency bonus (reward low variance)
      const scores = noteScores.map(ns => ns.score);
      const mean = averageScore;
      const variance = scores.reduce((sum, s) => sum + Math.pow(s - mean, 2), 0) / scores.length;
      const stdDev = Math.sqrt(variance);
      
      // Bonus: up to +5 points for consistent performance (low std dev)
      // stdDev of 0 = +5 points, stdDev of 20+ = 0 bonus
      const consistencyBonus = Math.max(0, 5 - (stdDev / 4));
      
      finalScore = Math.min(100, averageScore + consistencyBonus);
      
    } else {
      // Fallback: use old method if per-note data not available.
      // Apply the same proportional window logic (clamp(total_duration × 0.7, 0.5s, 3s))
      // so short lessons aren't penalised when falling through to this path.
      const totalDuration = level.notes.reduce((sum, note) => sum + note.duration, 0);

      if (logsToUse.accuracyHistory.length > 0) {
        const samplesPerSecond = logsToUse.accuracyHistory.length / totalDuration;
        const windowSec = Math.min(3, Math.max(0.5, totalDuration * 0.7));
        const windowSize = Math.floor(samplesPerSecond * windowSec);

        if (logsToUse.accuracyHistory.length >= windowSize) {
          for (let i = 0; i <= logsToUse.accuracyHistory.length - windowSize; i++) {
            const windowSamples = logsToUse.accuracyHistory.slice(i, i + windowSize);
            const windowAverage = windowSamples.reduce((sum, acc) => sum + acc, 0) / windowSamples.length;
            finalScore = Math.max(finalScore, windowAverage);
          }
        } else {
          finalScore = logsToUse.accuracyHistory.reduce((sum, acc) => sum + acc, 0) / logsToUse.accuracyHistory.length;
        }
      }
    }
    
    const score = Math.round(finalScore);
    const stars = calculateStars(score);
    
    // Calculate duration in seconds
    const duration = startTime ? Math.round((Date.now() - startTime) / 1000) : 0;
    
    // Calculate note accuracy breakdown
    let noteAccuracy = { perfect: 0, good: 0, missed: 0 };
    if (logsToUse.noteAccuracyData && logsToUse.noteAccuracyData.length > 0) {
      logsToUse.noteAccuracyData.forEach(noteData => {
        if (noteData && noteData.length > 0) {
          noteData.forEach(accuracy => {
            if (accuracy >= 90) noteAccuracy.perfect++;
            else if (accuracy >= 70) noteAccuracy.good++;
            else noteAccuracy.missed++;
          });
        }
      });
    }
    
    setGameState('complete');
    onComplete({ 
      score, 
      stars, 
      levelId: level.id,
      noteScores: noteScores.length > 0 ? noteScores : undefined,
      duration, // Add duration in seconds
      noteAccuracy, // Add accuracy breakdown
    });
  };

  const downloadDebugLog = async () => {
    const logsToUse = savedLogs || {
      debugLog: debugLogRef.current,
      accuracyHistory: accuracyHistoryRef.current,
      noteAccuracyData: noteAccuracyDataRef.current,
      metadata: {
        levelId: level.id,
        levelName: level.name,
        userSaFrequency: userSaFrequency,
        notes: level.notes
      }
    };
    
    // Calculate total duration by summing all note durations
    const totalDuration = level.notes.reduce((sum, note) => sum + note.duration, 0);
    
    // Calculate per-note scores if available
    let noteScores = [];
    if (logsToUse.noteAccuracyData && logsToUse.noteAccuracyData.length === level.notes.length) {
      noteScores = level.notes.map((note, index) => {
        const noteAccuracy = logsToUse.noteAccuracyData[index];
        const noteScore = calculateNoteScore(noteAccuracy, note.duration);
        return {
          noteIndex: index,
          noteDisplay: note.display,
          score: Math.round(noteScore),
          samples: noteAccuracy ? noteAccuracy.length : 0,
          averageAccuracy: noteAccuracy && noteAccuracy.length > 0
            ? Math.round(noteAccuracy.reduce((sum, acc) => sum + acc, 0) / noteAccuracy.length)
            : 0
        };
      });
    }
    
    const logData = {
      metadata: {
        ...logsToUse.metadata,
        timestamp: new Date().toISOString(),
        totalDuration: totalDuration,
        notes: logsToUse.metadata.notes.map(n => ({
          display: n.display,
          frequency: n.frequency,
          duration: n.duration
        }))
      },
      pitchDetectionLog: logsToUse.debugLog,
      accuracyHistory: logsToUse.accuracyHistory,
      noteAccuracyData: logsToUse.noteAccuracyData || [],
      noteScores: noteScores,
      finalScore: {
        score: Math.round(logsToUse.accuracyHistory.length > 0 
          ? logsToUse.accuracyHistory.reduce((sum, acc) => sum + acc, 0) / logsToUse.accuracyHistory.length 
          : 0),
        totalSamples: logsToUse.accuracyHistory.length,
        perNoteAverage: noteScores.length > 0
          ? Math.round(noteScores.reduce((sum, ns) => sum + ns.score, 0) / noteScores.length)
          : null
      }
    };

    const json = JSON.stringify(logData, null, 2);
    const filename = `pitch-log-level${level.id}-${Date.now()}.json`;

    // Use the native share sheet (works in WKWebView / iOS 15+)
    if (navigator.canShare) {
      const file = new File([json], filename, { type: 'application/json' });
      if (navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: filename });
          return;
        } catch (e) {
          // User cancelled or API error — fall through to data-URI
        }
      }
    }

    // Fallback: data URI (blob: URLs are blocked in WKWebView)
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(json);
    const a = document.createElement('a');
    a.href = dataUri;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const calculateStars = (score) => {
    // Adjusted scoring: 50% = 3.5 stars (realistic for singing accuracy)
    // Half-star increments for better granularity
    if (score >= 85) return 5.0;      // Excellent
    if (score >= 75) return 4.5;      // Very good
    if (score >= 65) return 4.0;      // Good
    if (score >= 55) return 3.5;      // Above average
    if (score >= 45) return 3.0;      // Average
    if (score >= 35) return 2.5;      // Below average
    if (score >= 25) return 2.0;      // Needs work
    if (score >= 15) return 1.5;      // Struggling
    if (score >= 5) return 1.0;       // Minimal
    return 0.5;                        // Incomplete
  };

  const startRecording = async () => {
    try {
      // Disable all speech-processing DSP — critical for Bluetooth HFP mics.
      // With these on, iOS/Android AGC and noise gates suppress the singing signal
      // before it reaches the analyser, causing misdetection and silent calibrations.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          channelCount: 1,
        },
      });
      setAudioStream(stream); // Store stream for pitch detection
      
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      
      let errorMessage = 'Microphone access denied. ';
      
      // Check if it's a security issue (HTTP on mobile)
      if (window.location.protocol === 'http:' && window.location.hostname !== 'localhost') {
        errorMessage += '\n\n⚠️ Microphone requires HTTPS on network devices.\n\n' +
                       'To fix:\n' +
                       '1. Stop the current server\n' +
                       '2. Run: ./dev_start_https.sh\n' +
                       '3. Access via https:// (accept security warning)\n\n' +
                       'Or enable microphone in browser settings.';
      } else {
        errorMessage += 'Please enable microphone permission in your browser settings.';
      }
      
      alert(errorMessage);
      onBack(); // Return to level selection
    }
  };

  const stopRecording = async () => {
    if (mediaRecorderRef.current && isRecording) {
      return new Promise((resolve) => {
        mediaRecorderRef.current.onstop = () => {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());
          setIsRecording(false);
          setAudioStream(null); // Clear stream
          resolve(audioBlob);
        };
        mediaRecorderRef.current.stop();
      });
    }
  };

  // Real-time pitch detection using Web Audio API + MPM (McLeod Pitch Method via pitchy)
  const startPitchDetection = async () => {
    if (!audioStream) {
      return;
    }

    const audioContext = new (window.AudioContext || window.webkitAudioContext)({ latencyHint: 'interactive' });
    audioContextRef.current = audioContext;
    // iOS WKWebView suspends AudioContext until a user-gesture chain — resume immediately
    if (audioContext.state === 'suspended') await audioContext.resume();

    // Adapt fftSize to the actual sample rate delivered by the device.
    // Bluetooth HFP can deliver 8000 or 16000 Hz; using 2048 there gives a
    // 256 ms frame which is far too slow. Smaller buffers keep latency ~50 ms.
    const sr = audioContext.sampleRate;
    const fftSize = sr <= 8000 ? 512 : sr <= 16000 ? 1024 : 2048;

    const analyser = audioContext.createAnalyser();
    analyser.fftSize = fftSize;
    analyserRef.current = analyser;

    const source = audioContext.createMediaStreamSource(audioStream);
    source.connect(analyser);

    // Create (or recreate) the MPM detector sized for this buffer
    pitchDetectorRef.current = PitchDetector.forFloat32Array(fftSize);

    const bufferLength = fftSize;
    const buffer = new Float32Array(bufferLength);
    
    // Initialize grace period timer immediately
    if (!noteStartTimeRef.current) {
      noteStartTimeRef.current = Date.now();
    }

    const detectPitch = () => {
      if (gameState !== 'playing') return;
      
      // Initialize grace period timer if not set (e.g., after note transition)
      if (!noteStartTimeRef.current) {
        noteStartTimeRef.current = Date.now();
      }
      
      // Check grace period every frame (not just when pitch detected).
      // Grace period is proportional to note duration so short notes (1-1.5s)
      // aren't still "preparing" when they finish: min(500ms, duration × 250ms).
      if (noteStartTimeRef.current) {
        const noteElapsed = Date.now() - noteStartTimeRef.current;
        const noteDuration = (currentNoteRef.current?.duration ?? 3) * 1000; // ms
        const graceMs = Math.min(500, noteDuration * 0.25);
        if (noteElapsed > graceMs && noteStateRef.current === 'preparing') {
          noteStateRef.current = 'active';
          setNoteState('active');
        }
      }

      analyser.getFloatTimeDomainData(buffer);

      // McLeod Pitch Method — returns [pitch, clarity] where clarity 0→1
      // indicates how voiced the frame is (< 0.6 is noise/silence).
      const [detectedPitchRaw, clarity] = pitchDetectorRef.current.findPitch(buffer, audioContext.sampleRate);
      const detectedPitch = clarity >= 0.6 && detectedPitchRaw > 0 ? detectedPitchRaw : -1;
      
      if (detectedPitch > 0) {
        // === STAGE 1: INTELLIGENT FREQUENCY VALIDATION ===
        const targetFreq = currentNoteRef.current.frequency;
        
        // Research-backed vocal range: 80-500 Hz covers male (80-300 Hz) and female (150-500 Hz)
        const ABSOLUTE_MIN_FREQ = 80;   // Below this is noise/artifacts
        const ABSOLUTE_MAX_FREQ = 500;  // Above this is likely harmonics
        
        // Calculate expected range around target (±1 octave allows for errors)
        const expectedMin = Math.max(targetFreq * 0.5, ABSOLUTE_MIN_FREQ);
        const expectedMax = Math.min(targetFreq * 2.0, ABSOLUTE_MAX_FREQ);
        expectedFreqRangeRef.current = { min: expectedMin, max: expectedMax };
        
        let validatedPitch = detectedPitch;
        let correctionApplied = 'none';
        
        // === STAGE 2: NOISE & ARTIFACT REJECTION ===
        // Reject extreme values that are clearly noise (should be rare with MPM clarity gate)
        if (detectedPitch < ABSOLUTE_MIN_FREQ) {
          setCurrentPitch(0);
          setCurrentConfidence(0);
          smoothedPitchRef.current = 0;
          if (Date.now() - lastValidPitchTimeRef.current > 150) pitchHistoryRef.current = [];
          if (!holdPitchTimerRef.current) {
            holdPitchTimerRef.current = setTimeout(() => { setDisplayPitch(0); holdPitchTimerRef.current = null; }, 400);
          }
          animationFrameRef.current = requestAnimationFrame(detectPitch);
          return;
        }
        
        if (detectedPitch > ABSOLUTE_MAX_FREQ) {
          setCurrentPitch(0);
          setCurrentConfidence(0);
          smoothedPitchRef.current = 0;
          if (Date.now() - lastValidPitchTimeRef.current > 150) pitchHistoryRef.current = [];
          if (!holdPitchTimerRef.current) {
            holdPitchTimerRef.current = setTimeout(() => { setDisplayPitch(0); holdPitchTimerRef.current = null; }, 400);
          }
          animationFrameRef.current = requestAnimationFrame(detectPitch);
          return;
        }
        
        // === STAGE 3: OCTAVE ERROR CORRECTION ===
        // Autocorrelation often detects octaves (half or double frequency)
        // Research shows phone mics frequently report octave below for male voices
        
        // Check if detected pitch is roughly octave below target
        const octaveBelow = targetFreq / 2;
        const octaveAbove = targetFreq * 2;
        
        if (Math.abs(detectedPitch - octaveBelow) < octaveBelow * 0.15) {
          // Within 15% of octave below - likely octave error
          validatedPitch = detectedPitch * 2;
          correctionApplied = 'octave_up';
        } else if (Math.abs(detectedPitch - octaveAbove) < octaveAbove * 0.15) {
          // Within 15% of octave above - correct down
          validatedPitch = detectedPitch / 2;
          correctionApplied = 'octave_down';
        }
        
        // Track octave corrections for adaptive learning
        octaveCorrectionHistoryRef.current.push({
          raw: detectedPitch,
          corrected: validatedPitch,
          correction: correctionApplied,
          target: targetFreq
        });
        if (octaveCorrectionHistoryRef.current.length > 20) {
          octaveCorrectionHistoryRef.current.shift();
        }
        
        // === STAGE 4: CONFIDENCE SCORING ===
        // Assign confidence based on how reasonable the frequency is
        let confidence = 1.0;
        const centsFromTarget = Math.abs(1200 * Math.log2(validatedPitch / targetFreq));
        
        // Reduce confidence if far from target
        if (centsFromTarget > 200) {
          confidence *= 0.5; // Very far - low confidence
        } else if (centsFromTarget > 100) {
          confidence *= 0.7; // Far - reduced confidence
        }
        
        // Reduce confidence if octave correction was applied
        if (correctionApplied !== 'none') {
          confidence *= 0.8;
        }
        
        confidenceHistoryRef.current.push(confidence);
        if (confidenceHistoryRef.current.length > 10) {
          confidenceHistoryRef.current.shift();
        }
        
        // Update current confidence for display
        const avgConfidence = confidenceHistoryRef.current.length > 0 
          ? confidenceHistoryRef.current.reduce((sum, c) => sum + c, 0) / confidenceHistoryRef.current.length 
          : 1.0;
        setCurrentConfidence(avgConfidence);
        
        // Skip low confidence readings — don't score, don't penalise
        if (confidence < 0.3) {
          setCurrentPitch(0);
          setCurrentConfidence(0);
          smoothedPitchRef.current = 0;
          if (Date.now() - lastValidPitchTimeRef.current > 150) pitchHistoryRef.current = [];
          if (!holdPitchTimerRef.current) {
            holdPitchTimerRef.current = setTimeout(() => { setDisplayPitch(0); holdPitchTimerRef.current = null; }, 400);
          }
          animationFrameRef.current = requestAnimationFrame(detectPitch);
          return;
        }
        
        // === STAGE 5: MULTI-LAYER SMOOTHING ===
        // Apply exponential smoothing weighted by confidence
        if (smoothedPitchRef.current === 0) {
          smoothedPitchRef.current = validatedPitch;
        } else {
          // Higher confidence = more weight to new reading
          const alpha = 0.2 + (confidence * 0.2); // 0.2-0.4 range
          smoothedPitchRef.current = smoothedPitchRef.current * (1 - alpha) + validatedPitch * alpha;
        }
        
        // Add to rolling window for median filter
        pitchHistoryRef.current.push(validatedPitch);
        if (pitchHistoryRef.current.length > 7) { // Increased window for more stability
          pitchHistoryRef.current.shift();
        }
        
        // Calculate median from rolling window
        const sortedPitches = [...pitchHistoryRef.current].sort((a, b) => a - b);
        const mid = Math.floor(sortedPitches.length / 2);
        const medianPitch = sortedPitches.length % 2 === 0
          ? (sortedPitches[mid - 1] + sortedPitches[mid]) / 2
          : sortedPitches[mid];
        
        // Use median for display and accuracy (more stable)
        const finalPitch = medianPitch;
        setCurrentPitch(finalPitch);
        // Hold-last-value: update display pitch and cancel any pending clear
        if (holdPitchTimerRef.current) {
          clearTimeout(holdPitchTimerRef.current);
          holdPitchTimerRef.current = null;
        }
        setDisplayPitch(finalPitch);
        lastValidPitchTimeRef.current = Date.now(); // Mark that we have a live signal
        
        // Calculate accuracy with more forgiving thresholds
        const centDeviation = Math.abs(1200 * Math.log2(finalPitch / targetFreq));
        
        // More forgiving accuracy calculation:
        // 0-30 cents = 100% accuracy (increased tolerance)
        // 30-60 cents = linear scale 100% -> 75%
        // 60-120 cents = linear scale 75% -> 50%
        // >120 cents = 50% -> 0%
        let accuracy;
        if (centDeviation <= 30) {
          accuracy = 100;
        } else if (centDeviation <= 60) {
          accuracy = 100 - (centDeviation - 30) * 0.833; // 25% drop over 30 cents
        } else if (centDeviation <= 120) {
          accuracy = 75 - (centDeviation - 60) * 0.417; // 25% drop over 60 cents
        } else {
          accuracy = Math.max(0, 50 - (centDeviation - 120) * 0.5);
        }
        
        // Console logging for debugging
        const logEntry = {
          timestamp: Date.now() - sessionStartTimeRef.current,
          raw: parseFloat(detectedPitch.toFixed(2)),
          validated: parseFloat(validatedPitch.toFixed(2)),
          smoothed: parseFloat(smoothedPitchRef.current.toFixed(2)),
          median: parseFloat(medianPitch.toFixed(2)),
          target: parseFloat(targetFreq.toFixed(2)),
          centsDev: parseFloat(centDeviation.toFixed(2)),
          accuracy: parseFloat(accuracy.toFixed(1)),
          confidence: parseFloat(confidence.toFixed(2)),
          avgConfidence: parseFloat(avgConfidence.toFixed(2)),
          correction: correctionApplied,
          validRange: `${expectedFreqRangeRef.current.min.toFixed(0)}-${expectedFreqRangeRef.current.max.toFixed(0)}`,
          rollingWindow: pitchHistoryRef.current.map(p => parseFloat(p.toFixed(1))),
          bufferSize: accuracyHistoryBufferRef.current.length
        };
        debugLogRef.current.push(logEntry);
        
        // === CALIBRATION DRIFT DETECTION ===
        // Track signed cents deviation (positive = singing sharp, negative = flat)
        const signedCents = 1200 * Math.log2(finalPitch / targetFreq);
        const drift = driftTrackingRef.current;
        drift.samples.push(signedCents);
        if (drift.samples.length > 60) drift.samples.shift(); // ~1 second window at 60fps
        if (!drift.warningShown && drift.samples.length >= 60) {
          const meanDrift = drift.samples.reduce((s, v) => s + v, 0) / drift.samples.length;
          // Warn if mean drift exceeds ±150 cents (roughly 1.5 semitones) consistently
          if (Math.abs(meanDrift) > 150) {
            drift.warningShown = true;
            setCalibrationDriftWarning(true);
          }
        }

        // Only score frames with real signal (confidence > floor of 0.5)
        // Floor-confidence frames are unreliable and drag down the average
        if (confidence > 0.5) {
          accuracyHistoryRef.current.push(accuracy);
          setAccuracyHistory((prev) => [...prev, accuracy]);
        }
        
      } else {
        // No voiced pitch — silence or noise.
        // Do NOT push a 0-accuracy sample: silence frames penalise a user for
        // natural breath gaps between notes and Bluetooth dropout. The score
        // is computed only over frames where the singer was actually producing
        // a pitch; absence-of-signal frames are neutral.
        setCurrentPitch(0);
        setCurrentConfidence(0);
        smoothedPitchRef.current = 0;
        // Only clear rolling window after a genuine gap (>150ms)
        if (Date.now() - lastValidPitchTimeRef.current > 150) pitchHistoryRef.current = [];
        // Hold-last-value: delay clearing display pitch by 400ms to prevent flicker
        if (!holdPitchTimerRef.current) {
          holdPitchTimerRef.current = setTimeout(() => {
            setDisplayPitch(0);
            holdPitchTimerRef.current = null;
          }, 400);
        }
      }

      animationFrameRef.current = requestAnimationFrame(detectPitch);
    };

    detectPitch();
  };

  const stopPitchDetection = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      const ctx = audioContextRef.current;
      audioContextRef.current = null;
      try { ctx.close().catch(() => {}); } catch (_) {}
    }
    // Reset smoothing and advanced detection
    smoothedPitchRef.current = 0;
    pitchHistoryRef.current = [];
    accuracyHistoryBufferRef.current = [];
    octaveCorrectionHistoryRef.current = [];
    confidenceHistoryRef.current = [];
  };

  const getNoteColor = (noteIndex) => {
    if (noteIndex < currentNoteIndex) return '#4caf50'; // completed
    if (noteIndex === currentNoteIndex) return '#58CC02'; // current
    return '#e0e0e0'; // upcoming
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Debug Log Download Button - Floating */}
      {(debugLogRef.current.length > 0 || savedLogs) && (
        <Box
          sx={{
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 1000,
          }}
        >
          <Button
            variant="contained"
            size="small"
            onClick={downloadDebugLog}
            sx={{
              backgroundColor: '#58CC02',
              color: 'white',
              fontSize: '0.75rem',
              boxShadow: 3,
              '&:hover': {
                backgroundColor: '#46a302',
              },
            }}
          >
            📥 Save Logs ({savedLogs ? savedLogs.debugLog.length : debugLogRef.current.length})
          </Button>
        </Box>
      )}

      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={onBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box flex={1}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Level {level.id}: {level.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {level.description}
          </Typography>
          {userSaFrequency && (
            <Typography variant="caption" sx={{ color: '#58CC02', display: 'block', mt: 0.5 }}>
              Sa: {userSaFrequency.toFixed(1)} Hz
            </Typography>
          )}
        </Box>
      </Box>

      {/* Note counter */}
      {gameState === 'playing' && (
        <Box mb={3}>
          <Typography variant="body2">
            Note {currentNoteIndex + 1} of {totalNotes}
          </Typography>
        </Box>
      )}

      {/* Main Content Area */}
      <Paper elevation={3} sx={{ p: 4, minHeight: 400, position: 'relative' }}>
        {/* INTRO State */}
        {gameState === 'intro' && (() => {
          const totalDuration = level.notes.reduce((sum, n) => sum + n.duration, 0);
          return (
            <Box textAlign="center" py={level.notes.length <= 5 ? 3 : 1.5}>
              {/* Title */}
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 0.5 }}>
                {hasHeardNote && !previewIsPlaying ? '✅ Ready to sing!' : '🔊 Hear the exercise first'}
              </Typography>
              <Typography variant="body2" sx={{ color: hasHeardNote && !previewIsPlaying ? '#58CC02' : '#aaa', mb: 2, transition: 'color 0.3s' }}>
                {hasHeardNote && !previewIsPlaying
                  ? 'Now tap START LEVEL and sing along!'
                  : previewIsPlaying
                    ? `Playing note ${previewNoteIdx + 1} of ${level.notes.length}…`
                    : 'Press play to hear all notes in order, then sing'}
              </Typography>

              {/* ── PRIMARY: Play Full Lesson button ──────────────────────────── */}
              <Button
                variant={previewIsPlaying ? 'outlined' : 'contained'}
                size="large"
                onClick={previewIsPlaying ? stopSequencePreview : startSequencePreview}
                sx={{
                  backgroundColor: previewIsPlaying ? 'transparent' : '#1565C0',
                  borderColor: previewIsPlaying ? '#1565C0' : undefined,
                  color: previewIsPlaying ? '#1565C0' : 'white',
                  fontSize: '1rem',
                  px: 3.5,
                  py: 1.2,
                  mb: 2.5,
                  borderRadius: 3,
                  fontWeight: 700,
                  boxShadow: previewIsPlaying ? 'none' : '0 4px 14px rgba(21,101,192,0.45)',
                  '&:hover': { backgroundColor: previewIsPlaying ? 'rgba(21,101,192,0.08)' : '#1976D2' },
                  // Pulsing attention ring until heard
                  '@keyframes listenRing': {
                    '0%,100%': { boxShadow: '0 4px 14px rgba(21,101,192,0.45)' },
                    '50%': { boxShadow: '0 4px 24px rgba(21,101,192,0.85)' },
                  },
                  animation: !hasHeardNote && !previewIsPlaying ? 'listenRing 1.8s ease-in-out infinite' : 'none',
                }}
              >
                {previewIsPlaying ? '⏹ Stop' : `▶ Play Full Exercise — ${totalDuration.toFixed(1)}s`}
              </Button>

              {/* ── Note chips: tap any individual note ──────────────────────── */}
              <Typography variant="caption" sx={{ display: 'block', color: '#666', mb: 1, fontSize: '0.7rem', letterSpacing: 0.5 }}>
                OR TAP A NOTE TO HEAR IT INDIVIDUALLY
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: level.notes.length <= 5 ? 1.5 : 0.75,
                  mb: 2,
                  flexWrap: 'wrap',
                }}
              >
                {level.notes.map((note, idx) => {
                  const isActive = playingRefNoteIdx === idx;
                  const pct = noteChipProgress[idx] ?? 0;
                  const isSequenceActive = previewIsPlaying && previewNoteIdx === idx;
                  return (
                    <Box
                      key={idx}
                      onClick={() => handleNotePreviewTap(note, idx)}
                      sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: 2,
                        px: 1.5,
                        py: 0.85,
                        minWidth: 44,
                        cursor: 'pointer',
                        userSelect: 'none',
                        backgroundColor: isActive ? '#2e7d32' : '#1a1a1a',
                        border: isActive || isSequenceActive
                          ? '2px solid #58CC02'
                          : '2px solid #333',
                        transform: isActive ? 'scale(1.12)' : 'scale(1)',
                        transition: 'all 0.15s',
                        '&:hover': { borderColor: '#58CC02', transform: 'scale(1.06)' },
                      }}
                    >
                      {/* Note name */}
                      <Typography sx={{ fontWeight: 'bold', fontSize: '0.95rem', color: isActive ? '#fff' : '#ddd', lineHeight: 1.2 }}>
                        {isActive ? '♪' : note.display}
                      </Typography>
                      {/* Duration label */}
                      <Typography sx={{ fontSize: '0.6rem', color: '#888', lineHeight: 1 }}>
                        {note.duration}s
                      </Typography>
                      {/* Progress fill — slides in from left during playback */}
                      {pct > 0 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: `${pct}%`,
                            height: 3,
                            backgroundColor: '#58CC02',
                            transition: 'width 0.04s linear',
                            borderRadius: '0 2px 0 0',
                          }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>

              {/* Meta info */}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {level.notes.length} note{level.notes.length !== 1 ? 's' : ''} · {totalDuration.toFixed(1)}s total
              </Typography>

              {userSaFrequency && (
                <Typography variant="caption" color="success.main" sx={{ display: 'block', mb: 2, fontWeight: 600 }}>
                  ✓ Calibrated to {userSaFrequency.toFixed(1)} Hz
                </Typography>
              )}

              {/* START LEVEL — locked until at least one note has been heard */}
              <Button
                variant="contained"
                size="medium"
                startIcon={<MicIcon />}
                disabled={!hasHeardNote}
                onClick={userSaFrequency ? startCountdown : onRequestCalibration}
                sx={{
                  backgroundColor: hasHeardNote ? '#58CC02' : '#444',
                  color: hasHeardNote ? 'white' : '#888',
                  fontSize: '1rem',
                  px: 3,
                  py: 1.2,
                  transition: 'background-color 0.4s ease, color 0.4s ease',
                  '&:hover': { backgroundColor: hasHeardNote ? '#46a302' : '#444' },
                  '&.Mui-disabled': { backgroundColor: '#444', color: '#888' },
                }}
              >
                {userSaFrequency ? 'Start Level' : 'Calibrate Voice & Start'}
              </Button>

              {!hasHeardNote && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#666' }}>
                  Hear the exercise above to unlock ↑
                </Typography>
              )}

              {userSaFrequency && hasHeardNote && (
                <Button
                  variant="text"
                  size="small"
                  onClick={onRequestCalibration}
                  sx={{ mt: 1, color: '#aaa', display: 'block', mx: 'auto', fontSize: '0.75rem' }}
                >
                  🎤 Recalibrate voice
                </Button>
              )}
            </Box>
          );
        })()}

        {/* CALIBRATION State */}
        {gameState === 'calibration' && (
          <Box textAlign="center" py={4}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#58CC02' }}>
              🎤 Voice Calibration
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={3}>
              Sing any comfortable note and hold it steady for 3 seconds
            </Typography>

            {/* Real-time pitch display */}
            <Box
              sx={{
                backgroundColor: '#1a1a1a',
                borderRadius: 2,
                p: 4,
                mb: 3,
                minHeight: 200,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {currentPitch > 0 ? (
                <>
                  <Typography variant="h2" sx={{ color: '#58CC02', fontWeight: 'bold', mb: 2 }}>
                    {currentPitch.toFixed(1)} Hz
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#aaa' }}>
                    Samples collected: {calibrationPitches.length}
                  </Typography>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: '#4caf50',
                      mt: 2,
                      animation: 'pulse 1s infinite',
                      '@keyframes pulse': {
                        '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                        '50%': { opacity: 0.5, transform: 'scale(1.3)' },
                      },
                    }}
                  />
                </>
              ) : (
                <>
                  <Typography variant="h6" sx={{ color: '#aaa', mb: 2 }}>
                    Listening...
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Please start singing
                  </Typography>
                </>
              )}
            </Box>

            <Button
              variant="contained"
              size="large"
              onClick={finishCalibration}
              disabled={calibrationPitches.length < 60} // ~3 seconds at 60fps
              sx={{
                backgroundColor: calibrationPitches.length >= 60 ? '#58CC02' : '#999',
                color: 'white',
                fontSize: '1.1rem',
                px: 4,
                py: 1.5,
                '&:hover': {
                  backgroundColor: calibrationPitches.length >= 60 ? '#46a302' : '#999',
                },
              }}
            >
              {calibrationPitches.length >= 60 ? 'Done! Start Level' : `Keep singing... (${Math.max(0, 60 - calibrationPitches.length)} more samples)`}
            </Button>

            <Button
              variant="text"
              onClick={() => {
                stopRecording();
                stopPitchDetection();
                setGameState('intro');
              }}
              sx={{ mt: 2, color: '#999' }}
            >
              Cancel
            </Button>
          </Box>
        )}

        {/* COUNTDOWN State */}
        {gameState === 'countdown' && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            minHeight={400}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: '8rem',
                fontWeight: 'bold',
                color: '#58CC02',
                animation: 'pulse 1s ease-in-out',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(1)', opacity: 0.5 },
                  '50%': { transform: 'scale(1.2)', opacity: 1 },
                  '100%': { transform: 'scale(1)', opacity: 0.5 },
                },
              }}
            >
              {countdown}
            </Typography>
          </Box>
        )}

        {/* PLAYING State */}
        {gameState === 'playing' && (
          <Box>
            {/* Passive drift indicator — tiny amber dot, non-blocking.
                Signals that drift was detected without interrupting the lesson. */}
            {calibrationDriftWarning && (
              <Box
                title="Pitch drift detected"
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: '#ffa726',
                  boxShadow: '0 0 6px rgba(255,167,38,0.8)',
                }}
              />
            )}

            {/* Current Note Display */}
            <Box textAlign="center" mb={1}>
              <Typography sx={{ fontWeight: 'bold', color: '#58CC02', fontSize: '1.5rem', lineHeight: 1.2 }}>
                {currentNote.display}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Target: {currentNote.frequency.toFixed(1)} Hz · {currentNote.duration}s
              </Typography>
            </Box>

            {/* PitchTracker - breaks out of Paper padding for full-width; Container's 16px acts as thin border */}
            <Box sx={{ mb: 1.5, mx: -4, borderRadius: 0, overflow: 'hidden' }}>
              <PitchTracker
                notes={calibratedNotes}
                currentNoteIndex={currentNoteIndex}
                currentFrequency={currentPitch}
                confidence={currentConfidence}
                noteState={noteState}
                isActive={isRecording}
                graceMs={1000}
                onProgress={handleNoteProgress}
              />
            </Box>

            {/* Current Pitch Display - natural height, fixed min to prevent layout shifts */}
            <Box textAlign="center" mb={1} sx={{ minHeight: 44, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: displayPitch > 0 ? '#58CC02' : '#555',
                  fontWeight: 'bold',
                  transition: 'color 0.2s',
                }}
              >
                {displayPitch > 0 ? `${displayPitch.toFixed(1)} Hz` : '· · ·'}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#aaa',
                  fontSize: '0.75rem',
                }}
              >
                {displayPitch > 0 ? 'your pitch' : 'sing to begin'}
              </Typography>
            </Box>

            {/* Note Progress Indicator */}
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#888', mt: 1.5, mb: 0.25, fontSize: '0.65rem', letterSpacing: 0.3 }}>
              Tap bubble to listen
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 0.75,
              }}
            >
              {level.notes.map((note, idx) => (
                <Box
                  key={idx}
                  onClick={() => playReferenceTone(getCalibFreq(note), idx)}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    backgroundColor: playingRefNoteIdx === idx
                      ? '#e65100'
                      : getNoteColor(idx),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.95rem',
                    transition: 'all 0.3s',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transform: idx === currentNoteIndex || playingRefNoteIdx === idx ? 'scale(1.15)' : 'scale(1)',
                    boxShadow: playingRefNoteIdx === idx
                      ? '0 4px 14px rgba(255,150,0,0.7)'
                      : idx === currentNoteIndex ? '0 4px 12px rgba(88,204,2,0.4)' : 'none',
                  }}
                >
                  {playingRefNoteIdx === idx ? '♪' : note.display}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* ── LANDSCAPE FULL-SCREEN OVERLAY ─────────────────────────────────
             Only visible when the device is in landscape + actively playing.
             Covers AppBar (zIndex 1400 > AppBar 1100) for an immersive view. */}
        {isLandscape && gameState === 'playing' && (
          <Box
            sx={{
              position: 'fixed',
              top: 0, left: 0, right: 0, bottom: 0,
              zIndex: 1400,
              backgroundColor: '#0a0a0a',
              display: 'flex',
              flexDirection: 'column',
              paddingLeft:   'env(safe-area-inset-left)',
              paddingRight:  'env(safe-area-inset-right)',
              paddingTop:    'env(safe-area-inset-top)',
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
          >
            {/* Passive drift dot for landscape — mirrors portrait indicator */}
            {calibrationDriftWarning && (
              <Box
                title="Pitch drift detected"
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: '#ffa726',
                  boxShadow: '0 0 6px rgba(255,167,38,0.8)',
                  zIndex: 10,
                }}
              />
            )}

            {/* PitchTracker — fills all remaining height, full width */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <PitchTracker
                notes={calibratedNotes}
                currentNoteIndex={currentNoteIndex}
                currentFrequency={currentPitch}
                confidence={currentConfidence}
                noteState={noteState}
                isActive={isRecording}
                graceMs={1000}
                onProgress={handleNoteProgress}
                height="100%"
              />
            </Box>

            {/* Note chips — tap to hear reference pitch */}
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#666', pt: 0.25, fontSize: '0.6rem', letterSpacing: 0.3 }}>
              Tap bubble to listen
            </Typography>
            <Box sx={{
              display: 'flex', justifyContent: 'center', flexWrap: 'wrap',
              gap: 0.75, px: 2, pb: 0.75, flexShrink: 0,
            }}>
              {level.notes.map((note, idx) => (
                <Box
                  key={idx}
                  onClick={() => playReferenceTone(getCalibFreq(note), idx)}
                  sx={{
                    width: 40, height: 40, borderRadius: 2,
                    backgroundColor: playingRefNoteIdx === idx ? '#e65100' : getNoteColor(idx),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontWeight: 'bold', fontSize: '0.85rem',
                    cursor: 'pointer', userSelect: 'none', transition: 'all 0.3s',
                    transform: idx === currentNoteIndex || playingRefNoteIdx === idx ? 'scale(1.15)' : 'scale(1)',
                    boxShadow: playingRefNoteIdx === idx
                      ? '0 4px 14px rgba(255,150,0,0.7)'
                      : idx === currentNoteIndex ? '0 4px 12px rgba(88,204,2,0.4)' : 'none',
                  }}
                >
                  {playingRefNoteIdx === idx ? '♪' : note.display}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* FINISHED State */}
        {gameState === 'finished' && (
          <Box textAlign="center" py={4}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              🎉 Level Complete!
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={3}>
              Great job! Download your performance data below if needed.
            </Typography>

            {/* Post-lesson drift suggestion — surfaces after the level ends, never during it */}
            {calibrationDriftWarning && (
              <Box
                sx={{
                  backgroundColor: '#fff8e1',
                  border: '1px solid #ffe082',
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                  mb: 3,
                  textAlign: 'left',
                }}
              >
                <Typography variant="body2" sx={{ color: '#795548', mb: 1 }}>
                  🎤 Your pitch seemed to drift during this lesson — this often means your voice range has shifted since calibration. Consider recalibrating before the next level.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={onRequestCalibration}
                    sx={{ color: '#795548', borderColor: '#ffe082', fontWeight: 'bold' }}
                  >
                    Recalibrate now
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setCalibrationDriftWarning(false);
                      driftTrackingRef.current.warningShown = true;
                    }}
                    sx={{ color: '#aaa' }}
                  >
                    Dismiss
                  </Button>
                </Box>
              </Box>
            )}
            
            {savedLogs && (
              <Box mb={3}>
                <Button
                  variant="outlined"
                  onClick={downloadDebugLog}
                  sx={{
                    color: '#58CC02',
                    borderColor: '#58CC02',
                    mb: 2,
                    '&:hover': {
                      borderColor: '#46a302',
                      backgroundColor: 'rgba(88, 204, 2, 0.04)',
                    },
                  }}
                >
                  📥 Download Debug Logs ({savedLogs.debugLog.length} samples)
                </Button>
              </Box>
            )}
            
            <Button
              variant="contained"
              size="large"
              onClick={showResults}
              sx={{
                backgroundColor: '#58CC02',
                color: 'white',
                fontSize: '1.2rem',
                px: 4,
                py: 1.5,
                '&:hover': {
                  backgroundColor: '#46a302',
                },
              }}
            >
              View Results
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default GameLevel;
